import { useEffect, useState } from 'react';
import { LogOut, Package, RefreshCw, AlertCircle, Search } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/hooks';
import KpiBar from '@/components/KpiBar';
import OrderDrawer from '@/components/OrderDrawer';
import NotificationBell from '@/components/NotificationBell';

type PhaseCounts = {
  phase0_count: number;
  phase1_count: number;
  phase2_count: number;
  phase3_count: number;
  delivered_count: number;
  cancelled_count: number;
  returned_count: number;
  delayed_count: number;
};

type OrderRow = {
  seller_order_id: string;
  buyer_order_id: string | null;
  stage: string;
  is_delayed: boolean;
  product_name: string | null;
  asin: string | null;
  depot_code: string | null;
  order_date: string | null;
  deadline_date: string | null;
  delivered_at: string | null;
  tracking_number?: string | null;
  carrier_name?: string | null;
};

const PHASE_LABEL: Record<string, string> = {
  yeni_siparis: 'Yeni — Buyer bekleniyor',
  satin_alindi: 'Satın alındı — Depoya yolda',
  depoda: 'Depoda hazırlanıyor',
  kargoda: 'Müşteriye kargoda',
  teslim: 'Teslim edildi',
  iptal: 'İptal',
  iade: 'İade',
  beklemede: 'Beklemede (eski)',
  gecikti: 'Geç (eski)',
};

const PHASE_BADGE: Record<string, string> = {
  yeni_siparis: 'bg-slate-100 text-slate-700 border border-slate-200',
  satin_alindi: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  depoda: 'bg-blue-50 text-blue-700 border border-blue-200',
  kargoda: 'bg-amber-50 text-amber-800 border border-amber-200',
  teslim: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  iptal: 'bg-rose-50 text-rose-700 border border-rose-200',
  iade: 'bg-purple-50 text-purple-700 border border-purple-200',
  beklemede: 'bg-slate-100 text-slate-700',
  gecikti: 'bg-red-100 text-red-700',
};

const STAGE_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'tümü', label: 'Tümü' },
  { value: 'yeni_siparis', label: 'Yeni' },
  { value: 'satin_alindi', label: 'Satın alındı' },
  { value: 'depoda', label: 'Depoda' },
  { value: 'kargoda', label: 'Kargoda' },
  { value: 'teslim', label: 'Teslim' },
  { value: 'iptal', label: 'İptal' },
  { value: 'iade', label: 'İade' },
];

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function daysAgo(s: string | null | undefined): number | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export default function Dashboard() {
  const { session } = useSession();
  const [counts, setCounts] = useState<PhaseCounts | null>(null);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [stageFilter, setStageFilter] = useState<string>('tümü');
  const [showOnlyDelayed, setShowOnlyDelayed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const { data: countsData, error: e1 } = await supabase
        .from('v_phase_counts')
        .select('*')
        .single();
      if (e1) throw e1;
      setCounts(countsData as PhaseCounts);

      let q = supabase
        .from('orders')
        .select('seller_order_id, buyer_order_id, stage, is_delayed, product_name, asin, depot_code, order_date, deadline_date, delivered_at')
        .order('order_date', { ascending: false, nullsFirst: false })
        .limit(200);

      if (stageFilter !== 'tümü') {
        q = q.eq('stage', stageFilter);
      }
      if (showOnlyDelayed) {
        q = q.eq('is_delayed', true);
      }
      if (searchQuery.trim()) {
        const s = searchQuery.trim();
        q = q.or(`seller_order_id.ilike.%${s}%,buyer_order_id.ilike.%${s}%,asin.ilike.%${s}%`);
      }

      const { data: ordersData, error: e2 } = await q;
      if (e2) throw e2;
      const rows = (ordersData as OrderRow[]) ?? [];

      // tracking_number ve carrier_name'i shipments'tan çek
      const ids = rows.map((r) => r.seller_order_id);
      if (ids.length > 0) {
        const { data: shipData } = await supabase
          .from('shipments')
          .select('seller_order_id, tracking_number, carrier_name')
          .in('seller_order_id', ids);
        const shipMap = new Map<string, { tracking_number: string | null; carrier_name: string | null }>();
        for (const s of shipData ?? []) {
          shipMap.set((s as { seller_order_id: string }).seller_order_id, s as { tracking_number: string | null; carrier_name: string | null });
        }
        for (const r of rows) {
          const s = shipMap.get(r.seller_order_id);
          if (s) {
            r.tracking_number = s.tracking_number;
            r.carrier_name = s.carrier_name;
          }
        }
      }

      setOrders(rows);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter, showOnlyDelayed]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => loadData(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  async function handleLogout() {
    await supabase.auth.signOut();
  }

  const totalActive = counts ? counts.phase0_count + counts.phase1_count + counts.phase2_count + counts.phase3_count : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Package className="w-5 h-5 text-slate-700 flex-shrink-0" />
            <h1 className="text-base font-semibold text-slate-900 truncate">Sipariş Takip</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 hidden md:inline">
              Son yenileme: {lastRefresh.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <button
              onClick={loadData}
              className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
              title="Yenile"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <NotificationBell />
            <span className="text-xs text-slate-500 hidden lg:inline">{session?.user.email}</span>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
              title="Çıkış"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* KPI satırı */}
        <KpiBar />

        {/* Faz sayıları */}
        {counts && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            <PhaseCard label="Yeni" sublabel="buyer bekliyor" count={counts.phase0_count} stage="yeni_siparis" />
            <PhaseCard label="Satın alındı" sublabel="depoya yolda" count={counts.phase1_count} stage="satin_alindi" />
            <PhaseCard label="Depoda" sublabel="hazırlanıyor" count={counts.phase2_count} stage="depoda" />
            <PhaseCard label="Kargoda" sublabel="müşteriye" count={counts.phase3_count} stage="kargoda" />
            <PhaseCard label="Teslim" sublabel="tamamlandı" count={counts.delivered_count} stage="teslim" />
            <PhaseCard label="İptal" sublabel="" count={counts.cancelled_count} stage="iptal" />
            <PhaseCard label="İade" sublabel="" count={counts.returned_count} stage="iade" />
            <PhaseCard label="Geç (flag)" sublabel="dikkat" count={counts.delayed_count} stage="gecikti" highlight />
          </div>
        )}

        {/* Filtreler + arama */}
        <div className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-slate-200 p-3">
          {STAGE_FILTERS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStageFilter(s.value)}
              className={`text-xs px-3 py-1 rounded-full transition ${
                stageFilter === s.value
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {s.label}
            </button>
          ))}
          <label className="ml-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyDelayed}
              onChange={(e) => setShowOnlyDelayed(e.target.checked)}
              className="rounded"
            />
            <span className="text-xs">Sadece geç</span>
          </label>
          <div className="ml-auto flex items-center gap-2 bg-slate-50 rounded-md px-2 py-1 border border-slate-200 w-full sm:w-auto">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Seller / Buyer / ASIN ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent outline-none text-xs w-full sm:w-64"
            />
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Sipariş tablosu — kompakt format */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Sipariş ID'leri</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Tarihler</th>
                  <th className="px-3 py-2 text-left">Aşama</th>
                  <th className="px-3 py-2 text-left">Depo</th>
                  <th className="px-3 py-2 text-left">Tracking</th>
                  <th className="px-3 py-2 text-left">Ürün</th>
                </tr>
              </thead>
              <tbody>
                {loading && orders.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Yükleniyor…</td></tr>
                )}
                {!loading && orders.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Bu filtrede sipariş yok.</td></tr>
                )}
                {orders.map((o) => {
                  const overdue = o.deadline_date && new Date(o.deadline_date) < new Date() && o.stage !== 'teslim' && o.stage !== 'iptal';
                  const overdueDays = overdue ? daysAgo(o.deadline_date) : null;
                  return (
                    <tr
                      key={o.seller_order_id}
                      onClick={() => setSelectedOrderId(o.seller_order_id)}
                      className="border-t border-slate-100 hover:bg-slate-50 align-top cursor-pointer"
                    >
                      {/* Sipariş ID'leri — alt alta */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-mono text-xs text-slate-900">{o.seller_order_id}</div>
                        <div className="font-mono text-xs text-slate-400 mt-0.5">
                          {o.buyer_order_id ?? <span className="italic text-slate-300">buyer yok</span>}
                        </div>
                      </td>
                      {/* Tarihler — alt alta */}
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        <div className="text-slate-700">
                          <span className="text-slate-400">Sipariş:</span> {fmtDate(o.order_date)}
                        </div>
                        <div className={`mt-0.5 ${overdue ? 'text-red-700 font-medium' : 'text-slate-700'}`}>
                          <span className="text-slate-400">Son tslm:</span> {fmtDate(o.deadline_date)}
                          {overdueDays !== null && overdueDays > 0 && (
                            <span className="ml-1 text-red-700">({overdueDays}g geç)</span>
                          )}
                        </div>
                      </td>
                      {/* Aşama */}
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${PHASE_BADGE[o.stage] ?? 'bg-slate-100 text-slate-700'}`}>
                          {PHASE_LABEL[o.stage] ?? o.stage}
                        </span>
                        {o.is_delayed && (
                          <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">
                            GEÇ
                          </span>
                        )}
                        {o.stage === 'teslim' && o.delivered_at && (
                          <div className="text-xs text-emerald-600 mt-0.5">
                            {fmtDate(o.delivered_at)}
                          </div>
                        )}
                      </td>
                      {/* Depo */}
                      <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">
                        {o.depot_code ?? <span className="text-slate-300">—</span>}
                      </td>
                      {/* Tracking */}
                      <td className="px-3 py-2 text-xs">
                        {o.tracking_number ? (
                          <div>
                            <div className="font-mono text-slate-900">{o.tracking_number}</div>
                            {o.carrier_name && <div className="text-slate-400 mt-0.5">{o.carrier_name}</div>}
                          </div>
                        ) : (
                          <span className="text-slate-300">çıkış yapmadı</span>
                        )}
                      </td>
                      {/* Ürün */}
                      <td className="px-3 py-2">
                        <div className="text-xs text-slate-700 max-w-xs truncate" title={o.product_name ?? ''}>
                          {o.product_name ?? '—'}
                        </div>
                        <div className="font-mono text-xs text-slate-400 mt-0.5">{o.asin ?? '—'}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="bg-slate-50 px-3 py-2 text-xs text-slate-500 border-t border-slate-100 flex items-center justify-between">
            <span>{orders.length} sipariş gösteriliyor (en son 200) — satıra tıklayınca detay açılır</span>
            {counts && (
              <span>
                Aktif: {totalActive} | Tamamlanan: {counts.delivered_count + counts.cancelled_count + counts.returned_count}
              </span>
            )}
          </div>
        </div>
      </main>

      <OrderDrawer
        sellerOrderId={selectedOrderId}
        open={selectedOrderId !== null}
        onClose={() => {
          setSelectedOrderId(null);
          loadData();
        }}
      />
    </div>
  );
}

function PhaseCard({ label, sublabel, count, stage, highlight }: {
  label: string;
  sublabel: string;
  count: number;
  stage: string;
  highlight?: boolean;
}) {
  return (
    <div className={`bg-white rounded-lg border p-3 ${highlight && count > 0 ? 'border-red-300 ring-1 ring-red-100' : 'border-slate-200'}`}>
      <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mb-1.5 ${PHASE_BADGE[stage] ?? 'bg-slate-100 text-slate-700'}`}>
        {label}
      </div>
      {sublabel && <div className="text-xs text-slate-400 mb-1">{sublabel}</div>}
      <div className="text-2xl font-semibold text-slate-900">{count}</div>
    </div>
  );
}
